<template>
  <main v-if="doctorsInfo" class="specialist">
    <div class="specialist__content">
      <div class="specialist__head wrapper">
        <div class="specialist__left">
          <CBreadcrumbs :parent-sections="breadcrumbs" :current-page-name="doctorsInfo?.title" />

          <h1 class="specialist__left-title">{{ doctorsInfo?.title }}</h1>
          <div v-if="doctorsInfo?.experience" class="specialist__left-desc">
            Стаж работы – {{ doctorsInfo?.experience }}
          </div>
          <div class="specialist__left-txt">
            {{ doctorsInfo.description }}
          </div>
          <div class="specialist__left-button">
            <CButtonTrigger
              button-title="Записаться"
              :button-trigger="{
                type: 'form',
                content: 'doctorsForm',
              }"
            />
          </div>
        </div>
        <div class="specialist__head-img">
          <div v-if="doctorsInfo?.link" class="video" @click="openMediaModal(0)"></div>
          <UIPictureTag
            :image-original="`${siteUrl}${doctorsInfo?.image.source}`"
            :image-webp="`${siteUrl}${doctorsInfo?.image.webp}`"
          />
        </div>
      </div>

      <section v-if="tabsNames?.length > 0" class="tabs">
        <div class="tabs__wrapper">
          <div class="tabs__header wrapper">
            <div
              v-for="(item, index) in tabsNames"
              :key="index"
              class="tabs__btn"
              :class="{ active: activeTab === index }"
              @click="activeTab = index"
            >
              <span>
                {{ item }}
              </span>
            </div>
          </div>
          <div class="tabs__content">
            <div
              v-for="(tabContent, tabIndex) in data.tabs"
              v-show="activeTab === tabIndex"
              :key="tabIndex"
              class="tabs__content-item"
              :class="{ 'text-tab': tabIndex === 0, dropdown: tabIndex === 0 && isShowTab }"
            >
              <button
                v-if="tabIndex === 0"
                class="button text-tab__btn"
                @click="isShowTab = !isShowTab"
              >
                <NuxtIcon name="IconArrowThinRight" />
              </button>
              <template v-for="(block, index) in tabContent.blocks" :key="block.id">
                <Component
                  v-bind="getBlocksProps(block, index)"
                  :is="articleBlocks[block.type]"
                  v-if="articleBlocks[block.type]"
                  :class="{ 'mt-0--i': block.type === 'BlockTextForm' }"
                />
              </template>
            </div>
          </div>
        </div>
      </section>
    </div>

    <COfficeBlock title="Адреса клиник" :offices="transformOffices" />
    <COnlineRegistration
      v-if="isOpenOnlineRegistrationModal"
      :is-open="isOpenOnlineRegistrationModal"
    />
  </main>
</template>

<script setup lang="ts">
import {
  computed,
  createError,
  onUnmounted,
  ref,
  useArticleBlocks,
  useAsyncData,
  useHead,
  useMediaModal,
  useRouter,
  useRuntimeConfig,
  useSeoMeta,
  useState,
} from '#imports';
import COfficeBlock from '~/components/blocks/officeBlock/COfficeBlock.vue';
import { type ApiDoctorPageResponse, type BlockOffice } from '~/types/pages';
import './styles.scss';
import CBreadcrumbs from '~/components/breadcrumbs/CBreadcrumbs.vue';
import type { ErrorResponse } from '~/server/utils/serverFetch';
import { isErrorResponse } from '~/server/utils/serverError';
import CButtonTrigger from '~/components/buttonTrigger/CButtonTrigger.vue';
import COnlineRegistration from '~/components/blocks/onlineRegistration/COnlineRegistration.vue';
import CDoctorOnlineRegistration from '~/components/pages/doctorDetail/CDoctorOnlineRegistration.vue';
import { useSchemaOrgPage } from '~/composables/useSchemaOrgPage';
import { absoluteUrl, organizationId } from '~/utils/schemaOrg';

const affiliates = useState<BlockOffice['offices']>('affiliates');
const config = useState('config');
const transformOffices = ref([]);
const route = useRouter().currentRoute.value;
const emit = defineEmits(['error-not-found']);
const slug = computed(() => {
  {
    if (route.path === '/') {
      return 'index';
    }

    if (route.params && route.params.chapters) {
      return (route.params.chapters as string[]).join('/');
    }
  }
});
const { siteUrl } = useRuntimeConfig().public;

const { data, error } = await useAsyncData<ErrorResponse | ApiDoctorPageResponse>(
  `getDoctorDetailContent:${slug.value}`,
  () => {
    return $fetch('/api/doctors/extended', {
      method: 'GET',
      params: {
        slug: slug.value,
      },
    });
  },
);

if (error.value) {
  emit('error-not-found');
}

const tabsNames = ref();
const doctorsInfo = ref();
const breadcrumbs = ref();
const isOpenOnlineRegistrationModal = ref(false);
const transformBreadcrumbs = data => {
  const items = data.breadcrumbs?.map((el, index) => ({
    id: index,
    slug: el.link,
    title: el.title,
  }));

  if (items?.length >= 2) {
    items?.splice(-1, 1);
  }

  return items;
};

if (data.value) {
  if (data.value.status === 404) {
    emit('error-not-found');
  }

  if (!isErrorResponse(data.value)) {
    tabsNames.value = data.value.tabs.map(el => el.name);
    doctorsInfo.value = {
      id: data.value.id,
      title: data.value.title,
      image: data.value.image,
      image_seo: data.value.image_seo,
      category: data.value.category,
      extra_categories: data.value.extra_categories,
      link: data.value.link,
      experience: data.value.experience,
      description: data.value.description,
      mediaOrientation: data.value.media_orientation,
    };

    breadcrumbs.value = transformBreadcrumbs(data.value);

    useHead({
      title: data.value.seo.title || data.value.title,
    });

    useSeoMeta({
      description: data.value.seo.description.replace(/[""'`']/g, ''),
      keywords: data.value.seo.keywords.replace(/[""'`']/g, ''),
      ogTitle: data.value.og.title.replace(/[""'`']/g, ''),
      ogDescription: data.value.og.description.replace(/[""'`']/g, ''),
      ogImage: data.value?.og.image.source
        ? `${siteUrl}${data.value.og.image.source}`
        : '/android-chrome-192x192.png',
      ogUrl: route.fullPath,
    });

    const pageUrl = absoluteUrl(siteUrl, route.path);
    const doctorId = `${pageUrl.replace(/\/$/, '')}/#doctor`;
    const nameParts = data.value.title.split(' ').filter(Boolean);

    useSchemaOrgPage({
      template: 'doctors-detail',
      title: data.value.seo.title || data.value.title,
      description: data.value.seo.description,
      entity: {
        '@id': doctorId,
        '@type': ['Person', 'Physician'],
        name: data.value.title,
        medicalSpecialty: 'https://schema.org/Dentistry',
        worksFor: { '@id': organizationId(siteUrl) },
        familyName: nameParts[0],
        givenName: nameParts[1],
        additionalName: nameParts.slice(2).join(' ') || undefined,
        image: data.value.image?.source ? `${siteUrl}${data.value.image.source}` : undefined,
        description: data.value.description || data.value.seo.description,
      },
    });
  } else {
    emit('error-not-found');
  }
}

if (affiliates.value) {
  transformOffices.value = affiliates.value.map(el => ({
    title: el.title,
    workTime: el.working_time,
    address: el.address,
    phone: el.phone,
    lat: el.latitude,
    lon: el.longitude,
    image: el.image,
    rating: el.blocks[0],
  }));
}

const activeTab = ref(0);

const { articleBlocks, getBlocksProps } = useArticleBlocks();

const isShowTab = ref(false);

const dataSource = computed(() => ({
  html:
    doctorsInfo.value?.mediaOrientation === 'horizontal'
      ? `<iframe src="${doctorsInfo.value?.link}?rel=0"
                    frameborder="0"
                    allow="
                     accelerometer;
                     autoplay;
                     clipboard-write;
                     encrypted-media;
                     gyroscope;
                     picture-in-picture;
                     web-share"
                    allowfullscreen>
                   </iframe>`
      : `<div class="reelse__frame">
            <iframe
              src="${doctorsInfo.value?.link}&hd=2"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture;" frameborder="0" allowfullscreen>
            </iframe>
         </div>`,
}));

const { openMediaModal, destroyMediaModal } = useMediaModal([dataSource.value]);

onUnmounted(() => destroyMediaModal());
</script>
